import { createApiClient } from "./api-client";
import { coachingEndpoints } from "./coaching-contracts";
import type { CoachingProfileDto } from "./coaching-dtos";

const defaultProductBaseUrl = "/api/coaching";
const defaultAuthBaseUrl = "https://api-agents.vjal.ai";
const coachingApiBaseUrl = resolveCoachingApiBaseUrl({
  coachingApiBaseUrl: import.meta.env.VITE_COACHING_API_BASE_URL,
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
  apiPrefix: import.meta.env.VITE_API_PREFIX
});

export const coachingApi = createApiClient({
  baseUrl: coachingApiBaseUrl
});

/**
 * Platform auth lane (OTP send/verify, session logout). Central OTP must stay on
 * api-agents.vjal.ai; the coaching-local OTP/logout routes are removed 410 shells.
 */
export const coachingAuthApi = createApiClient({
  baseUrl: resolveAuthApiBaseUrl({
    authApiBaseUrl: import.meta.env.VITE_AUTH_API_BASE_URL,
    authApiPrefix: import.meta.env.VITE_AUTH_API_PREFIX
  })
});

export function resolveAuthApiBaseUrl({
  authApiBaseUrl,
  authApiPrefix
}: {
  authApiBaseUrl?: string;
  authApiPrefix?: string;
}) {
  const base = cleanBaseUrl(authApiBaseUrl) || defaultAuthBaseUrl;
  const prefix = cleanBaseUrl(authApiPrefix);
  return `${base}${prefix}`;
}

export function getCurrentCoachingProfile() {
  return coachingApi.request<CoachingProfileDto>(coachingEndpoints["auth.me"].pathTemplate);
}

export function resolveCoachingApiBaseUrl({
  coachingApiBaseUrl,
  apiBaseUrl,
  apiPrefix
}: {
  coachingApiBaseUrl?: string;
  apiBaseUrl?: string;
  apiPrefix?: string;
}) {
  const explicit = cleanBaseUrl(coachingApiBaseUrl);
  if (explicit) return explicit;

  // VITE_API_PREFIX is honored for symmetry with VITE_AUTH_API_PREFIX. An unset
  // prefix falls back to the conventional "/api/coaching"; an explicitly empty
  // prefix ("") means "the base URL is already prefixed, append nothing".
  const prefix = apiPrefix === undefined ? "/api/coaching" : cleanBaseUrl(apiPrefix);
  const productRoot = cleanBaseUrl(apiBaseUrl);
  if (!productRoot) return prefix || defaultProductBaseUrl;
  if (!prefix || productRoot.endsWith(prefix)) return productRoot;
  return `${productRoot}${prefix}`;
}

function cleanBaseUrl(value: string | undefined) {
  // Linear trailing-slash trim (sonarjs super-linear-regex class, 2026-07-06).
  let v = value?.trim() ?? "";
  while (v.endsWith("/")) v = v.slice(0, -1);
  return v;
}
